import { Row, Col, Form, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TimeFrameSelector from './TimeFrameSelector';
import SortSelector from './SortSelector';

const priorityOptions = [
  { value: 'all', label: 'All Priorities' },
  { value: 'URGENT', label: 'Urgent Only' },
  { value: 'HIGH', label: 'High Priority' },
  { value: 'MEDIUM', label: 'Medium Priority' },
  { value: 'LOW', label: 'Low Priority' },
  { value: 'OK', label: 'OK / No Action' }
];

const recommendationOptions = [
  { value: 'all', label: 'All Recommendations' },
  { value: 'Increase', label: 'Increase FC' },
  { value: 'Decrease', label: 'Decrease FC' },
  { value: 'No Change', label: 'No Change' }
];

const analysisTypeOptions = [
  { value: '', label: 'All Analysis Types' },  // No filter - shows all data
  { value: 'SINGLE_SKU', label: 'Single-SKU Orders (Most Accurate)' },
  { value: 'MULTI_SKU', label: 'Multi-SKU Orders' },
  { value: 'ALL', label: 'All Orders (Combined)' }
];

// FC Method selector - determines which FC value is displayed
const fcMethodOptions = [
  { value: 'SS1', label: 'SS1 (Recommended)', description: 'Single-unit orders only' },
  { value: 'SSN', label: 'SSN Per-Unit', description: 'Multi-quantity per-unit cost' },
  { value: 'MS', label: 'MS Allocated', description: 'Multi-SKU allocated cost' },
  { value: 'BLENDED', label: 'Blended', description: 'Order-mix weighted average' }
];

const strategyOptions = [
  { value: '', label: 'All Strategies' },
  { value: 'AGGRESSIVE', label: 'Aggressive (P65)' },
  { value: 'MODERATE', label: 'Moderate (P70)' },
  { value: 'BALANCED', label: 'Balanced (P75)' },
  { value: 'CONSERVATIVE', label: 'Conservative (P80)' },
  { value: 'PROFILE_BASED', label: 'Profile-Based' }
];

const temperatureOptions = [
  { value: '', label: 'All Categories' },
  { value: 'AMBIENT', label: 'Ambient' },
  { value: 'REFRIGERATED', label: 'Refrigerated (+$15)' },
  { value: 'FROZEN', label: 'Frozen (+$25)' }
];

const serviceLevelOptions = [
  { value: '', label: 'All Service Levels' },
  { value: 'GROUND', label: 'Ground' },
  { value: 'EXPEDITED', label: 'Expedited (Any)' },
  { value: 'TWO_DAY', label: '2-Day' },
  { value: 'THREE_DAY', label: '3-Day' },
  { value: 'NEXT_DAY', label: 'Next Day' }
];

export default function FilterBar({
  filters = {},
  onFilterChange,
  selectedCount = 0,
  timeFrame,
  onTimeFrameChange,
  sortKey = 'priority',
  onSortChange,
  fcMethod = 'SS1',
  onFCMethodChange
}) {
  return (
    <div className="px-3 py-2 border-bottom bg-light">
      <Row className="g-2 align-items-center">
        {/* FC Method Selector - Primary control for which FC value to display */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            size="sm"
            value={fcMethod}
            onChange={(e) => onFCMethodChange?.(e.target.value)}
            className="fw-semibold"
            title="Select which FC calculation method to display"
          >
            {fcMethodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>

        {/* Search */}
        <Col xs={12} md={4} lg={3}>
          <InputGroup size="sm">
            <InputGroup.Text>
              <FontAwesomeIcon icon="search" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search SKU or product..."
              value={filters.search || ''}
              onChange={(e) => onFilterChange({ search: e.target.value })}
            />
            {filters.search && (
              <Button
                variant="outline-secondary"
                onClick={() => onFilterChange({ search: '' })}
              >
                <FontAwesomeIcon icon="times" />
              </Button>
            )}
          </InputGroup>
        </Col>

        {/* Priority Filter */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            size="sm"
            value={filters.priority || 'all'}
            onChange={(e) => onFilterChange({ priority: e.target.value })}
          >
            {priorityOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>

        {/* Recommendation Filter */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            size="sm"
            value={filters.recommendation || 'all'}
            onChange={(e) => onFilterChange({ recommendation: e.target.value })}
          >
            {recommendationOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>

        {/* Analysis Type Filter */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            size="sm"
            value={filters.analysisType ?? ''}
            onChange={(e) => onFilterChange({ analysisType: e.target.value })}
            title="Filter by order type analysis"
          >
            {analysisTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>

        {/* Strategy Filter */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            size="sm"
            value={filters.strategy ?? ''}
            onChange={(e) => onFilterChange({ strategy: e.target.value })}
            title="Filter by pricing strategy"
          >
            {strategyOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>

        {/* Temperature Category Filter */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            size="sm"
            value={filters.temperatureCategory ?? ''}
            onChange={(e) => onFilterChange({ temperatureCategory: e.target.value })}
            title="Filter by temperature category"
          >
            {temperatureOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>

        {/* Service Level Filter */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            size="sm"
            value={filters.serviceLevel ?? ''}
            onChange={(e) => onFilterChange({ serviceLevel: e.target.value })}
            title="Filter by dominant service level"
          >
            {serviceLevelOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>

        {/* Time Frame Selector */}
        <Col xs="auto">
          <TimeFrameSelector
            value={timeFrame}
            onChange={onTimeFrameChange}
          />
        </Col>

        {/* Sort Selector */}
        <Col xs={6} md={3} lg={2}>
          <SortSelector
            value={sortKey}
            onChange={onSortChange}
          />
        </Col>

        {/* Spacer */}
        <Col className="d-none d-lg-block" />

        {/* Selected Count Badge (Export is in header) */}
        {selectedCount > 0 && (
          <Col xs="auto">
            <span className="badge bg-primary">
              {selectedCount} selected
            </span>
          </Col>
        )}
      </Row>
    </div>
  );
}
